import React, { useCallback, useMemo, useRef, useState } from 'react';
import cl from 'classnames';
import reduce from 'lodash/reduce';

import { IconsEnum } from '../../../../assets/icons/types';

import { Icon } from '../../../../helpers/Icon';
import { Translate } from '../../../../helpers/Translate';
import { LoadMoreButtonHelper } from '../../../../helpers/buttons/LoadMoreButtonHelper';

import { composerKeys } from '../../../../locales/keys';

import { TeamNanoID, TeamUUID } from '../../../teams/teamsTypes';

import {
  FETCH_PRODUCTS_QUERY,
  FetchProductsQueryResponse
} from '../../../products/queries/fetchProducts.query';
import { CACHE_FETCH_PRODUCTS_QUERY } from '../../../products/queries/fetchCacheProducts.query';
import { CACHE_FETCH_SHOW_PRODUCT_QUERY } from '../../../products/queries/fetchCacheShowProduct.query';
import { FETCH_SHOW_PRODUCT_QUERY } from '../../../products/queries/fetchShowProduct.query';

import { useCacheProducts } from '../../../products/hooks/useCacheProducts';
import { useProductsFiltersCache } from '../../../products/hooks/useProductsFiltersCache';

import { ProductCache } from '../../../products/ProductCache';
import { ProductsPermissions } from '../../../products/productsConstants';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../products/productsTypes';

import { ThreeDStockProductsFilters } from '../../../stock/components/filters/ThreeDStockProductsFilters';

import {
  ComposerDockProductItem,
  ComposerDockProductItemProduct
} from '../ComposerDockProductItem';

import { mapProductToDockItem } from './utils/mapProductToDockItem';
import {
  useComposerProductsDockCategories,
  ComposerProductsDockCategory
} from './hooks/useComposerProductsDockCategories';
import { useComposerDockHeight } from '../../hooks/useComposerDockHeight';

const MIN_DOCK_HEIGHT = 120;
const HEADER_HEIGHT = 40;
const DOCK_INITIAL_LIMIT = 48;
const MAXIMIZED_HEIGHT_RATIO = 0.8;

interface ComposerProductsDockProps {
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
  onAddProductToScene: (product: ComposerDockProductItemProduct) => void;
  onDraggedProductChange: (
    product: ComposerDockProductItemProduct | null
  ) => void;
}

function ComposerProductsDock({
  companyNanoId,
  companyUuid,
  onAddProductToScene,
  onDraggedProductChange
}: ComposerProductsDockProps) {
  const productsCacheKey = useMemo(
    () =>
      companyNanoId
        ? ProductCache.companyLibraryCacheKey(companyNanoId)
        : ProductCache.indexCacheKey(),
    [companyNanoId]
  );

  const initialFilters = useMemo<FetchProductsFilters>(
    () => ({
      blocked: false,
      ...(companyUuid ? { teamIds: [companyUuid] } : {})
    }),
    [companyUuid]
  );

  const { cachedProductsFilters, cachedProductsSort } = useProductsFiltersCache(
    {
      cacheKey: productsCacheKey,
      initialFilters
    }
  );

  const {
    products,
    productsTotalCount,
    productsFetched,
    productsFetchingNextPage,
    productsFilters,
    hasNextProductsPage,
    loadMoreProducts,
    changeProductsFilters,
    filterProducts,
    productsPage,
    productsLimit
  } = useCacheProducts<FetchProductsQueryResponse>({
    cacheKey: productsCacheKey,
    cacheQuery: CACHE_FETCH_PRODUCTS_QUERY,
    fetchCacheItemQuery: CACHE_FETCH_SHOW_PRODUCT_QUERY,
    fetchItemCacheKey: ProductCache.showCacheKey(),
    fetchItemQuery: FETCH_SHOW_PRODUCT_QUERY,
    initialFilters: cachedProductsFilters,
    initialLimit: DOCK_INITIAL_LIMIT,
    initialSort: cachedProductsSort,
    query: FETCH_PRODUCTS_QUERY,
    trackTotalHits: true
  });

  const { checkedCategories, setCheckedCategories } =
    useComposerProductsDockCategories();

  // The dock reuses ThreeDStockProductsFilters, whose category filter expects a
  // dedicated `changeCategory` handler. On the full stock page that also resets
  // search/sort state per tab; in the dock we only ever operate on the products
  // grid, so it collapses to a plain filter change.
  const handleChangeCategory = useCallback<
    (changeFilters?: FetchProductsFilters, removeFilters?: string[]) => void
  >(
    (changeFilters, removeFilters) => {
      changeProductsFilters(changeFilters ?? {}, removeFilters);
    },
    [changeProductsFilters]
  );

  // Mirrors handleClearFilterProducts on the full stock page: reset to the
  // dock's initial filters while keeping the requested fields from the
  // current filters (e.g. search stays when only categories are cleared).
  const handleClearFilters = useCallback<
    (persistFiltersFields?: FetchProductsFilterFields[]) => void
  >(
    (persistFiltersFields) => {
      const persistFilters = reduce(
        persistFiltersFields,
        (result, field) => {
          result[field] = productsFilters?.[field];
          return result;
        },
        { ...initialFilters }
      );

      filterProducts(persistFilters);
    },
    [filterProducts, initialFilters, productsFilters]
  );

  const handleSetCheckedCategories = useCallback(
    (categories: ComposerProductsDockCategory[]) => {
      setCheckedCategories(categories);
    },
    [setCheckedCategories]
  );

  const dockItems = useMemo<ComposerDockProductItemProduct[]>(
    () => (products || []).map(mapProductToDockItem),
    [products]
  );

  // Dock UI state — open/collapsed, resizable height, maximized. Open state
  // and height (as a ratio of the dock's parent) persist globally across
  // every Compose session via useComposerDockHeight, independent of which
  // products the scene was opened with.
  const { isDockOpen, setIsDockOpen, dockHeightRatio, setDockHeightRatio } =
    useComposerDockHeight();
  const [isDockMaximized, setIsDockMaximized] = useState(false);
  const restoreHeightRatioRef = useRef(dockHeightRatio);
  const resizeRef = useRef<HTMLDivElement | null>(null);
  const resizeStateRef = useRef<{ startY: number; startHeight: number } | null>(
    null
  );

  const getParentHeight = useCallback(() => {
    return (
      resizeRef.current?.closest('[data-compose-dock]')?.parentElement
        ?.clientHeight ?? window.innerHeight
    );
  }, []);

  const dockHeight = Math.max(
    Math.round(getParentHeight() * dockHeightRatio),
    MIN_DOCK_HEIGHT
  );

  const toggleDockOpen = useCallback(() => {
    setIsDockOpen(!isDockOpen);
  }, [isDockOpen, setIsDockOpen]);

  const handleToggleMaximize = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDockOpen(true);
      setIsDockMaximized((prevMaximized) => {
        if (prevMaximized) {
          setDockHeightRatio(restoreHeightRatioRef.current);
          return false;
        }
        restoreHeightRatioRef.current = dockHeightRatio;
        setDockHeightRatio(MAXIMIZED_HEIGHT_RATIO);
        return true;
      });
    },
    [dockHeightRatio, setDockHeightRatio, setIsDockOpen]
  );

  // Drag-resize from the top edge. Listeners live on window so the drag keeps
  // tracking even when the cursor leaves the thin handle; cleaned up on stop.
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      resizeStateRef.current = { startY: e.clientY, startHeight: dockHeight };

      const parentHeight = getParentHeight();
      const maxHeight = Math.round(parentHeight * MAXIMIZED_HEIGHT_RATIO);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const state = resizeStateRef.current;
        if (!state) return;
        const delta = state.startY - moveEvent.clientY;
        const nextHeight = Math.min(
          Math.max(state.startHeight + delta, MIN_DOCK_HEIGHT),
          maxHeight
        );
        setDockHeightRatio(nextHeight / parentHeight);
        setIsDockMaximized(false);
      };

      const handleMouseUp = () => {
        resizeStateRef.current = null;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [dockHeight, getParentHeight, setDockHeightRatio]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, product: ComposerDockProductItemProduct) => {
      e.dataTransfer.effectAllowed = 'copy';
      onDraggedProductChange(product);
    },
    [onDraggedProductChange]
  );

  const handleDragEnd = useCallback(() => {
    onDraggedProductChange(null);
  }, [onDraggedProductChange]);

  const isEmpty = productsFetched && productsTotalCount === 0;

  return (
    <div
      data-compose-dock
      data-open={isDockOpen || undefined}
      className="relative shrink-0 flex flex-col border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 z-1"
      style={{ height: isDockOpen ? dockHeight : HEADER_HEIGHT }}
    >
      <div
        ref={resizeRef}
        data-compose-dock-resize
        onMouseDown={handleResizeMouseDown}
        className="absolute -top-1.5 inset-x-0 h-3 z-50 cursor-ns-resize opacity-0 hover:opacity-40 active:opacity-20 bg-gray-500 dark:bg-blue-500 select-none transition-opacity duration-300 ease-out hover:delay-200"
      />

      <div
        data-compose-dock-header
        onClick={toggleDockOpen}
        className="flex items-center gap-2 px-2 py-1.5 shrink-0 cursor-pointer select-none"
      >
        <Icon
          icon={IconsEnum.CHEVRON_UP}
          className={cl(
            'h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200',
            { 'rotate-180': !isDockOpen }
          )}
        />
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          <Translate id={composerKeys.productsLibrary} />
        </span>
        <span className="hidden lg:inline text-2xs text-gray-400 dark:text-gray-500">
          <Translate id={composerKeys.productsLibraryHint} />
        </span>
        <button
          type="button"
          data-compose-dock-max
          onClick={handleToggleMaximize}
          className="ml-auto p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <Icon
            icon={
              isDockMaximized
                ? IconsEnum.COLLAPSE_ALL_OUTLINE
                : IconsEnum.ARROWS_EXPAND_OUTLINE
            }
            className="h-4 w-4 text-gray-500 dark:text-gray-400"
          />
        </button>
      </div>

      {isDockOpen && (
        <div
          data-compose-dock-body
          className="flex-1 relative min-h-0 border-t border-gray-200 dark:border-gray-700"
        >
          <div className="absolute inset-0 flex divide-x divide-gray-200 dark:divide-gray-700">
            {/* The stock filters layout positions itself absolute inset-0, so a
                relative, fixed-width, overflow-hidden wrapper keeps it scoped to
                this dock column instead of the whole modal. */}
            <div className="relative w-72 shrink-0 h-full overflow-hidden">
              <ThreeDStockProductsFilters
                teamUuid={companyUuid}
                productsFilters={productsFilters}
                changeProductsFilters={
                  changeProductsFilters as ChangeProductsFiltersFunc
                }
                filterProducts={filterProducts}
                clearFilters={handleClearFilters}
                checkedCategories={checkedCategories}
                setCheckedCategories={handleSetCheckedCategories}
                changeCategory={handleChangeCategory}
                withSearchByNameFilter
                withoutCategoryDrilldown
                isCompact
              />
            </div>

            <div className="flex-1 relative">
              <div className="absolute inset-0 overflow-y-auto">
                {isEmpty ? (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-gray-400 dark:text-gray-500">
                    <Translate id={composerKeys.noProductsFound} />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-auto-fill grid-cell-min-28 2xl:grid-cell-min-32 gap-2 p-3">
                      {dockItems.map((product) => (
                        <ComposerDockProductItem
                          key={product.id}
                          product={product}
                          onAddToScene={onAddProductToScene}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                        />
                      ))}
                    </div>

                    {hasNextProductsPage && (
                      <div className="flex justify-center pb-3">
                        <LoadMoreButtonHelper
                          action={
                            ProductsPermissions.READ_PRODUCTS_LOAD_MORE_BUTTON_ITEMS_COUNT
                          }
                          itemsFetchingNextPage={productsFetchingNextPage}
                          itemsTotalCount={productsTotalCount}
                          limit={productsLimit}
                          loadMoreItems={loadMoreProducts}
                          page={productsPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComposerProductsDock;
